import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Formik, Form as FormikForm } from 'formik';
import { useTranslation } from 'react-i18next';

import Screen from '../../components/common/Screen';
import Card from '../../components/elements/Card';
import Button from '../../components/elements/Button';
import Title from '../../components/common/Title';
import Icon from '../../components/common/Icon';
import NotFound from '../../components/common/NotFound';
import { apiRequest, shortDate } from '../../utils';
import FormThankYou from './FormThankYou';

// Map a server-side validation error code to a translation key. The server is
// authoritative; client-side checks below just mirror it so the user gets
// instant feedback. Unknown codes fall back to a generic invalid message.
const SERVER_ERROR_KEYS = {
  ERR_REQUIRED: 'formErrorRequired',
  ERR_NOT_A_NUMBER: 'formErrorNumber',
  ERR_INVALID_EMAIL: 'formErrorEmail',
  ERR_INVALID_DATE: 'formErrorDate',
  ERR_INVALID_OPTION: 'formErrorInvalid',
  ERR_INVALID_VALUE: 'formErrorInvalid',
  ERR_CONSENT_REQUIRED: 'formErrorConsent',
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const PublicForm = () => {
  const { t, i18n } = useTranslation();
  const { slug } = useParams();

  const [schema, setSchema] = useState(null);
  const [status, setStatus] = useState('loading'); // loading | ok | notfound
  const [done, setDone] = useState(null); // { thank_you_message }

  // Embedded mode (?embed=1): render chrome-less and report our height to the
  // parent window so the embed snippet can auto-size the iframe. Captured once —
  // usePersistUrlParams moves the query param into sessionStorage + cleans the URL.
  const [isEmbed] = useState(() =>
    new URLSearchParams(window.location.search).get('embed') === '1' ||
    sessionStorage.getItem('embed') === '1'
  );

  // WHICH event this form was opened from (?event=<id>). It is not decoration: it is
  // what stamps SurveyAnswer.event_id, and without it every answer given here rolls up
  // under "not linked to an event" — which is what made the per-event numbers empty.
  // Captured at mount for the same reason as embed above: usePersistUrlParams moves the
  // param into sessionStorage and cleans the URL a tick later. A stale value from an
  // earlier form is harmless — the server drops an event this survey is not attached to.
  const [eventId] = useState(() =>
    new URLSearchParams(window.location.search).get('event') ||
    sessionStorage.getItem('event') ||
    undefined
  );

  useEffect(() => {
    if (!slug) { setStatus('notfound'); return undefined; }
    let cancelled = false;
    setStatus('loading');
    apiRequest('GET', `/public-form/${slug}`)
      .then((res) => { if (!cancelled) { setSchema(res); setStatus('ok'); } })
      .catch(() => { if (!cancelled) setStatus('notfound'); });
    return () => { cancelled = true; };
  }, [slug]);

  // Embed: transparent background + post document height to the parent whenever
  // the content resizes (schema load, validation errors, thank-you, images).
  useEffect(() => {
    if (!isEmbed) return undefined;
    document.documentElement.style.background = 'transparent';
    document.body.style.background = 'transparent';
    document.body.style.margin = '0';
    const post = () => {
      const h = Math.ceil(document.body.scrollHeight || 0);
      if (h && window.parent) {
        window.parent.postMessage({ type: 'tornea-form-height', slug, height: h }, '*');
      }
    };
    post();
    let ro;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(post);
      ro.observe(document.body);
    }
    window.addEventListener('load', post);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener('load', post);
    };
  }, [isEmbed, slug, status, done, schema]);

  // The form is the brand's page, not ours: scope --primary-color to the owning BU's
  // colour so the submit button and the primary accents wear it (PLG pink rather than
  // the Tornea orange). Scoped to this page's wrapper, the same way the storefront
  // checkout does it — Bootstrap.css paints .btn-primary and the checked boxes from
  // that variable. Null (no BU colour, or the placeholder black) leaves the default.
  const accent = (schema && schema.primary_color) || null;
  const accentStyle = accent ? { '--primary-color': accent } : undefined;

  // Chrome-less wrapper for embed; the normal full-screen shell otherwise.
  const wrap = (content) =>
    isEmbed
      ? <div className="tornea-form-embed" style={{ padding: '10px', maxWidth: '640px', margin: '0 auto', ...(accentStyle || {}) }}>{content}</div>
      : <Screen><div className="top" style={accentStyle}>{content}</div></Screen>;

  // A way out. Standalone this page has no nav bar and no back link, so someone who
  // opened it from a mail can only close the tab. Signed in, home is their own app
  // home; otherwise it is the brand's storefront (the form tells us which brand, and
  // the slug a storefront visit left behind is the fallback), else the login page.
  // Never in embed mode — there the form is a fragment of someone else's page.
  //
  // That is only a GUESS, and the form can override it: hide_back_home drops the button
  // entirely (a form running inside someone else's campaign does not want a second exit
  // beside Submit), and back_home_url sends it somewhere specific — the customer's own
  // site, say. An explicit setting wins for everyone, including a signed-in member;
  // half-honouring it would be the surprising behaviour.
  const homeTo = () => {
    if (localStorage.getItem('isLoggedIn') === '1') return '/home';
    const brand = (schema && schema.brand_slug) || localStorage.getItem('brandSlug');
    return brand ? `/${brand}` : '/';
  };

  const configuredHome = schema && schema.back_home_url;
  // An absolute URL leaves the app, so it cannot go through the router's <Link>. The
  // mutation only ever stores http(s):// or a leading-slash path, so these two cover it.
  const isExternalHome = /^https?:\/\//i.test(configuredHome || '');

  const homeButton = (isEmbed || (schema && schema.hide_back_home)) ? null : (
    <Button
      to={isExternalHome ? null : (configuredHome || homeTo())}
      onClick={isExternalHome ? () => window.location.assign(configuredHome) : undefined}
      theme="secondary" mb="0" className="mt-3"
    >
      <Icon name="home" className="me-2" />
      {t('backToHome')}
    </Button>
  );

  // ----- loading / not found -----
  if (status === 'loading') {
    return wrap(<NotFound translationKey="loading" loading={true} />);
  }

  if (status === 'notfound' || !schema) {
    return wrap(
      <Card>
        <Title>
          <Icon name="info" className="me-2" theme="primary" />
          {t('formNotFoundTitle')}
        </Title>
        <p className="text-muted">{t('formNotFound')}</p>
        {homeButton}
      </Card>
    );
  }

  // ----- thank-you state -----
  if (done) {
    return <FormThankYou message={done.thank_you_message} embed={isEmbed} homeButton={homeButton} accent={accent} />;
  }

  const fields = schema.fields || [];

  // What I said last time, when the server recognised me and this is a poll I have already
  // answered (see routes/forms.py). Present = this page is an EDIT, and every difference below
  // hangs off that one fact.
  const mine = schema.my_answers || null;
  const previous = (mine && mine.values) || {};

  // Build initial Formik values per field type, then lay my previous answers over them. A
  // question added to the poll since I answered simply keeps its empty default.
  const initialValues = { hp: '', consent: !!mine };
  fields.forEach((f) => {
    const was = previous[f.id];
    const empty = f.type === 'multiselect' ? [] : '';
    if (was === undefined || was === null) { initialValues[f.id] = empty; return; }
    initialValues[f.id] = f.type === 'multiselect' ? (Array.isArray(was) ? was : []) : was;
  });

  // Client-side validation mirroring the server (server stays authoritative).
  const validate = (values) => {
    const errors = {};
    fields.forEach((f) => {
      const v = values[f.id];
      const empty =
        v === undefined || v === null ||
        (typeof v === 'string' && v.trim() === '') ||
        (Array.isArray(v) && v.length === 0);

      if (empty) {
        if (f.required) errors[f.id] = t('formErrorRequired');
        return;
      }
      if ((f.type === 'number' || f.type === 'rating') && Number.isNaN(Number(v))) {
        errors[f.id] = t('formErrorNumber');
      } else if (f.type === 'email' && !EMAIL_RE.test(String(v).trim())) {
        errors[f.id] = t('formErrorEmail');
      } else if (f.type === 'singleselect' && !f.options.includes(String(v))) {
        errors[f.id] = t('formErrorInvalid');
      } else if (f.type === 'multiselect') {
        const bad = (Array.isArray(v) ? v : []).some((opt) => !f.options.includes(opt));
        if (bad) errors[f.id] = t('formErrorInvalid');
      }
    });
    if (schema.require_consent && !values.consent) {
      errors.consent = t('formErrorConsent');
    }
    return errors;
  };

  // Leave for the form's configured redirect_url. Standalone that is a plain
  // navigation; embedded it has to be the HOST page — the loader script listens
  // for this message and moves window.top. The delayed self-navigation is the
  // safety net for a page still running an older loader (which would silently
  // drop the message and strand the visitor on the submitted form).
  const redirectTo = (url) => {
    if (!isEmbed) { window.location.href = url; return; }
    try {
      window.parent.postMessage({ type: 'tornea-form-redirect', slug, url }, '*');
    } catch (e) { /* no parent to talk to — the fallback below still fires */ }
    setTimeout(() => { window.location.href = url; }, 1500);
  };

  const handleSubmit = async (values, { setSubmitting, setErrors, setStatus: setFormikStatus }) => {
    const answers = fields
      .map((f) => ({ field_id: f.id, value: values[f.id] }))
      .filter(({ value }) =>
        !(value === '' || value === undefined || value === null ||
          (Array.isArray(value) && value.length === 0))
      );

    // CRM attribution: the tracked-email redirect appends ?t=<signed token> to the
    // form URL. usePersistUrlParams moves it into sessionStorage and cleans the URL,
    // so read it from there (fall back to the live query string). Sent with the
    // submission so the server ties this fill-out to the contact from the email.
    const trackingToken =
      sessionStorage.getItem('t') ||
      new URLSearchParams(window.location.search).get('t') ||
      undefined;

    // Submit directly via fetch (not apiRequest) so we can read the body on a
    // 400 for the field-keyed error map, and so a validation failure only hits
    // the rate-limited endpoint once.
    try {
      const resp = await fetch(`/api/public-form/${slug}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          submit_token: schema.submit_token,
          answers,
          consent: !!values.consent,
          hp: values.hp || '',
          t: trackingToken,
          event_id: eventId,
        }),
      });

      if (resp.ok) {
        const data = await resp.json();
        // Redirect off-site if the form is configured to, else show thank-you.
        // Embedded, navigating ourselves would load the target site INSIDE the
        // iframe — hand the URL to the embed loader so the host page navigates.
        if (data.redirect_url) { redirectTo(data.redirect_url); return; }
        // A form's own thank-you text ("we'll be in touch") is written for a first fill-out, so
        // an edit gets its own sentence unless the author wrote one.
        setDone({
          thank_you_message: data.thank_you_message
            || (data.updated ? t('formAnswersUpdated') : undefined),
        });
        return;
      }

      if (resp.status === 400) {
        // {errors: {field_id|consent: CODE}}
        const data = await resp.json().catch(() => ({}));
        if (data && data.errors) {
          const mapped = {};
          Object.entries(data.errors).forEach(([k, code]) => {
            mapped[k] = t(SERVER_ERROR_KEYS[code] || 'formErrorInvalid');
          });
          setErrors(mapped);
          return;
        }
      }

      setFormikStatus(t('formSubmitError'));
    } catch (e) {
      setFormikStatus(t('formSubmitError'));
    } finally {
      setSubmitting(false);
    }
  };

  const renderField = (f, { values, errors, touched, handleChange, handleBlur, setFieldValue }) => {
    const err = touched[f.id] && errors[f.id];
    const common = {
      id: `field_${f.id}`,
      name: f.id,
      onBlur: handleBlur,
      className: `form-control${err ? ' is-invalid' : ''}`,
    };

    let input;
    switch (f.type) {
      case 'longtext':
        input = (
          <textarea {...common} rows={4} value={values[f.id]} onChange={handleChange}
                    placeholder={f.placeholder || ''} />
        );
        break;
      case 'number':
        input = (
          <input {...common} type="number" value={values[f.id]} onChange={handleChange}
                 placeholder={f.placeholder || ''} />
        );
        break;
      case 'rating': {
        // A NUMERIC_RATING survey question — a 1..maximum slider (like surveys).
        const rmax = f.maximum || 5;
        const rmin = 1;
        const raw = values[f.id];
        const cur = (raw === '' || raw == null) ? Math.round((rmin + rmax) / 2) : Number(raw);
        input = (
          <div>
            <input
              type="range"
              min={rmin}
              max={rmax}
              value={raw === '' || raw == null ? cur : raw}
              className={`form-range${err ? ' is-invalid' : ''}`}
              onChange={(e) => setFieldValue(f.id, Number(e.target.value))}
              onBlur={handleBlur}
            />
            <div className="d-flex justify-content-between small text-muted mt-1">
              <span>{rmin}</span>
              <div className="text-center"><b>({cur})</b></div>
              <span>{rmax}</span>
            </div>
          </div>
        );
        break;
      }
      case 'date':
        input = (
          <input {...common} type="date" value={values[f.id]} onChange={handleChange} />
        );
        break;
      case 'email':
        input = (
          <input {...common} type="email" value={values[f.id]} onChange={handleChange}
                 placeholder={f.placeholder || ''} />
        );
        break;
      case 'phone':
        input = (
          <input {...common} type="tel" value={values[f.id]} onChange={handleChange}
                 placeholder={f.placeholder || ''} />
        );
        break;
      case 'yesno':
        input = (
          <div className={err ? 'is-invalid' : ''}>
            {['yes', 'no'].map((opt) => (
              <div className="form-check form-check-inline" key={opt}>
                <input
                  className="form-check-input"
                  type="radio"
                  name={f.id}
                  id={`field_${f.id}_${opt}`}
                  checked={values[f.id] === opt}
                  onChange={() => setFieldValue(f.id, opt)}
                  onBlur={handleBlur}
                />
                <label className="form-check-label" htmlFor={`field_${f.id}_${opt}`}>
                  {opt === 'yes' ? t('yes') : t('no')}
                </label>
              </div>
            ))}
          </div>
        );
        break;
      case 'singleselect':
        input = (
          <div className={err ? 'is-invalid' : ''}>
            {f.options.map((opt, idx) => (
              <div className="form-check" key={`${f.id}_${idx}`}>
                <input
                  className="form-check-input"
                  type="radio"
                  name={f.id}
                  id={`field_${f.id}_${idx}`}
                  checked={values[f.id] === opt}
                  onChange={() => setFieldValue(f.id, opt)}
                  onBlur={handleBlur}
                />
                <label className="form-check-label" htmlFor={`field_${f.id}_${idx}`}>{opt}</label>
              </div>
            ))}
          </div>
        );
        break;
      case 'multiselect':
        input = (
          <div className={err ? 'is-invalid' : ''}>
            {f.options.map((opt, idx) => {
              const selected = Array.isArray(values[f.id]) && values[f.id].includes(opt);
              return (
                <div className="form-check" key={`${f.id}_${idx}`}>
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id={`field_${f.id}_${idx}`}
                    checked={selected}
                    onChange={() => {
                      const cur = Array.isArray(values[f.id]) ? values[f.id] : [];
                      setFieldValue(f.id, selected ? cur.filter((o) => o !== opt) : [...cur, opt]);
                    }}
                    onBlur={handleBlur}
                  />
                  <label className="form-check-label" htmlFor={`field_${f.id}_${idx}`}>{opt}</label>
                </div>
              );
            })}
          </div>
        );
        break;
      case 'shorttext':
      default:
        input = (
          <input {...common} type="text" value={values[f.id]} onChange={handleChange}
                 placeholder={f.placeholder || ''} />
        );
        break;
    }

    return (
      <div className="mb-4 text-start" key={f.id}>
        <label className="form-label fw-bold" htmlFor={`field_${f.id}`}>
          {f.label}{f.required && <span className="text-danger"> *</span>}
        </label>
        {input}
        {f.help_text && (
          <div className="form-text" style={{ fontSize: '13px' }}>{f.help_text}</div>
        )}
        {err && <div className="invalid-feedback d-block">{errors[f.id]}</div>}
      </div>
    );
  };

  return wrap(
    <Card>
          {schema.picture_url && (
            <img
              src={schema.picture_url}
              alt=""
              className="img-fluid rounded mb-3"
              style={{ width: '100%', maxHeight: '240px', objectFit: 'cover' }}
            />
          )}
          <Title>{schema.title}</Title>
          {schema.description && (
            <p className="text-muted text-start mx-2">{schema.description}</p>
          )}

          {/* Says it out loud before they read a single question: this is not a fresh form, it
              is what you already told them, and saving replaces it rather than adding a second
              answer. Without the line a prefilled form looks like somebody else's data. */}
          {mine && (
            <p className="text-start mx-2 fw-bold">
              <Icon name="check" theme="primary" className="me-2" />
              {mine.answered_at
                ? t('formAnsweredOn', { date: shortDate(mine.answered_at, i18n.language) })
                : t('formAnsweredAlready')}
            </p>
          )}

          <Formik
            initialValues={initialValues}
            validate={validate}
            onSubmit={handleSubmit}
          >
            {(formik) => (
              <FormikForm noValidate>
                {fields.map((f) => renderField(f, formik))}

                {/* Honeypot — hidden from humans, irresistible to bots. */}
                <div
                  aria-hidden="true"
                  style={{ position: 'absolute', left: '-9999px', top: 'auto', width: '1px', height: '1px', overflow: 'hidden' }}
                >
                  <label htmlFor="hp">{t('formLeaveBlank')}</label>
                  <input
                    type="text"
                    id="hp"
                    name="hp"
                    tabIndex={-1}
                    autoComplete="off"
                    value={formik.values.hp}
                    onChange={formik.handleChange}
                  />
                </div>

                {/* Consent */}
                {schema.require_consent && (
                  <div className="mb-3 text-start">
                    <div className="form-check">
                      <input
                        className={`form-check-input${formik.touched.consent && formik.errors.consent ? ' is-invalid' : ''}`}
                        type="checkbox"
                        id="consent"
                        name="consent"
                        checked={formik.values.consent}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                      />
                      <label className="form-check-label" htmlFor="consent">
                        {schema.consent_text || t('formConsentDefault')}
                      </label>
                      {formik.touched.consent && formik.errors.consent && (
                        <div className="invalid-feedback d-block">{formik.errors.consent}</div>
                      )}
                    </div>
                  </div>
                )}

                {formik.status && (
                  <div className="alert alert-danger" role="alert">{formik.status}</div>
                )}

                <Button type="submit" disabled={formik.isSubmitting}>
                  {formik.isSubmitting ? t('loading') : (mine ? t('saveMyAnswers') : t('submit'))}
                </Button>
              </FormikForm>
            )}
          </Formik>
          {homeButton}
    </Card>
  );
};

export default PublicForm;
